import React, { Fragment, useState, useEffect, useContext, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { getAllProduct } from "../../admin/products/FetchApi";
import { HomeContext } from "./index";
import { isWishReq, unWishReq, isWish } from "./Mixins";
import { FaStar, FaShoppingCart } from "react-icons/fa";
import { getRandomFallbackImage } from "../../../utils/randomImage";
import { useCart } from "../../../contexts/CartContext";

const apiURL = process.env.REACT_APP_API_URL;

const InfiniteProductScroll = () => {
  const { data, dispatch } = useContext(HomeContext);
  const history = useHistory();
  const { addToCart } = useCart();
  
  const [allProducts, setAllProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [wList, setWlist] = useState(
    JSON.parse(localStorage.getItem("wishList"))
  );

  const observerTarget = useRef(null);
  const PRODUCTS_PER_PAGE = 20;

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    addToCart(product);
  };

  // Fetch tous les produits une seule fois
  useEffect(() => {
    fetchAllProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger les 20 premiers produits au départ ou quand les produits changent
  useEffect(() => {
    if (data.products && data.products.length > 0) {
      setAllProducts(data.products);
      const firstBatch = data.products.slice(0, PRODUCTS_PER_PAGE);
      setDisplayedProducts(firstBatch);
      setPage(1);
      setHasMore(data.products.length > PRODUCTS_PER_PAGE);
      setInitialLoading(false);
    }
  }, [data.products]);

  const fetchAllProducts = async () => {
    setInitialLoading(true);
    try {
      let responseData = await getAllProduct();
      if (responseData && responseData.Products) {
        dispatch({ type: "setProducts", payload: responseData.Products });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setInitialLoading(false);
    }
  };

  // Fonction pour charger plus de produits
  const loadMoreProducts = useCallback(() => {
    if (loading || !hasMore) return;

    setLoading(true);
    
    setTimeout(() => {
      const startIndex = page * PRODUCTS_PER_PAGE;
      const endIndex = startIndex + PRODUCTS_PER_PAGE;
      const newProducts = allProducts.slice(startIndex, endIndex);

      if (newProducts.length > 0) {
        setDisplayedProducts(prev => [...prev, ...newProducts]);
        setPage(prev => prev + 1);
        setHasMore(endIndex < allProducts.length);
      } else {
        setHasMore(false);
      }
      
      setLoading(false);
    }, 500); // Petit délai pour simuler le chargement
  }, [page, allProducts, loading, hasMore]);

  // Intersection Observer pour détecter le scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMoreProducts();
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [loadMoreProducts, hasMore, loading]);

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <img
            className="w-16 h-16 animate-spin"
            src="/images/favicon.png"
            alt="Chargement"
          />
          <p className="text-base text-gray-600 font-medium">Chargement des produits...</p>
        </div>
      </div>
    );
  }

  if (!displayedProducts || displayedProducts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <svg className="w-20 h-20 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
        <p className="text-xl font-semibold text-gray-700 mb-2">Aucun produit disponible</p>
        <p className="text-gray-500 mb-6">Revenez bientôt pour découvrir nos nouveautés !</p>
        <button
          onClick={fetchAllProducts}
          className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-lg"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="space-y-8">
        {/* En-tête section produits */}
        <div className="flex items-center justify-between border-b border-gray-200 pb-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-blue-800">
              Tous nos produits
            </h2>
            <p className="text-sm md:text-base text-gray-600 mt-1">
              {allProducts.length} {allProducts.length > 1 ? 'produits disponibles' : 'produit disponible'}
            </p>
          </div>
        </div>

        {/* Grille de produits avec scroll infini */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
          {displayedProducts.map((item, idx) => {
            let currentAverageRating = 0;
            let currentTotalReviews = item.pRatingsReviews ? item.pRatingsReviews.length : 0;

            if (currentTotalReviews > 0) {
              const totalRatingSum = item.pRatingsReviews.reduce(
                (sum, review) => sum + parseInt(review.rating, 10),
                0
              );
              currentAverageRating = parseFloat((totalRatingSum / currentTotalReviews).toFixed(1));
            }

            const offer = parseInt(item.pOffer, 10) || 0;
            const basePrice = Number(item.pPrice) || 0;
            const discountedPrice = offer > 0
              ? Math.max(0, Math.round(basePrice * (1 - offer / 100)))
              : basePrice;

            return (
              <div 
                key={`${item._id}-${idx}`} 
                className="group relative flex flex-col overflow-hidden rounded-2xl border-2 border-gray-400 bg-white shadow-sm hover:shadow-xl hover:border-blue-400 transition-all duration-300 transform hover:-translate-y-1 h-full"
              >
                {/* Badge promo */}
                {offer > 0 && (
                  <span className="absolute right-0 top-2 z-10 text-xs font-bold bg-yellow-600 from-red-500 to-pink-500 text-red px-3 py-1 rounded-full shadow-lg">
                    -{offer}%
                  </span>
                )}

                {/* Wishlist et Panier */}
                <div className="absolute right-2 top-2 z-10 flex gap-2">
                  {/* Bouton Panier */}
                  <button
                    onClick={(e) => handleAddToCart(e, item)}
                    className="bg-blue-800 hover:bg-blue-700 p-2 rounded-full shadow-lg hover:scale-110 transition-all duration-300"
                    title="Ajouter au panier"
                  >
                    <FaShoppingCart className="text-white w-5 h-5" />
                  </button>

                  {/* Bouton Wishlist */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isWish(item._id, wList)) {
                        unWishReq(e, item._id, setWlist);
                      } else {
                        isWishReq(e, item._id, setWlist);
                      }
                    }}
                    className={`${
                      isWish(item._id, wList) 
                        ? 'bg-red-500 hover:bg-red-600' 
                        : 'bg-white hover:bg-gray-100'
                    } p-2 rounded-full shadow-lg hover:scale-110 transition-all duration-300`}
                    title={isWish(item._id, wList) ? "Retirer des favoris" : "Ajouter aux favoris"}
                  >
                    <svg
                      className={`w-5 h-5 ${isWish(item._id, wList) ? 'text-white' : 'text-red-500'}`}
                      fill={isWish(item._id, wList) ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
                      />
                    </svg>
                  </button>
                </div>

                {/* Image produit - Taille fixe */}
                <div
                  className="relative w-full h-64 sm:h-72 md:h-80 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 cursor-pointer flex-shrink-0"
                  onClick={() => history.push(`/products/${item._id}`)}
                >
                  <img
                    src={`${apiURL}/uploads/products/${item.pImages?.[0]}`}
                    alt={item.pName}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = getRandomFallbackImage();
                    }}
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Badge "Nouveau" pour les 10 premiers produits */}
                  {idx < 10 && (
                    <span className="absolute bottom-2 left-2 bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded-lg">
                      Nouveau
                    </span>
                  )}
                </div>

                {/* Infos produit - Hauteur fixe */}
                <div className="p-2 md:p-3 space-y-0.5 bg-blue-100 hover:bg-yellow-200 flex flex-col flex-grow">
                  <h3 
                    className="text-sm md:text-base font-semibold text-gray-900 line-clamp-2 h-9 md:h-11 cursor-pointer hover:text-blue-600 transition-colors mb-0.5"
                    onClick={() => history.push(`/products/${item._id}`)}
                  >
                    {item.pName}
                  </h3>

                  {/* Rating */}
                  <div className="flex items-center gap-1">
                    <div className="flex text-yellow-400 text-xs md:text-sm">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <FaStar key={i} className={i < Math.round(currentAverageRating) ? '' : 'text-gray-300'} />
                      ))}
                    </div>
                    <span className="text-xs text-gray-500 ml-1">
                      ({currentTotalReviews > 0 ? currentAverageRating : '5.0'})
                    </span>
                  </div>

                  {/* Prix - Poussé vers le bas */}
                  <div className="flex items-center gap-2 flex-wrap mt-auto">
                    {offer > 0 && (
                      <span className="line-through text-red-400 text-xs md:text-sm">
                        {basePrice.toLocaleString()} F
                      </span>
                    )}
                    <span className="text-blue-800 font-bold text-base md:text-lg">
                      {discountedPrice.toLocaleString()} F
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Loading indicator */}
        {loading && (
          <div className="flex justify-center py-8">
            <div className="flex flex-col items-center gap-3">
              <img
                className="w-12 h-12 animate-spin"
                src="/images/favicon.png"
                alt="Chargement"
              />
              <p className="text-sm text-gray-500">Chargement de plus de produits...</p>
            </div>
          </div>
        )}

        {/* Observer target */}
        <div ref={observerTarget} className="h-10" />

        {/* Message fin de liste */}
        {!hasMore && displayedProducts.length > 0 && (
          <div className="text-center py-2 border-t border-gray-200 mt-1">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-yellow-50 rounded-full">
              <svg className="w-3.5 h-3.5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-700 font-medium text-xs">
                Vous avez vu tous nos {allProducts.length} produits !
              </span>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default InfiniteProductScroll;
